import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ReactNode, createContext, useMemo } from 'react'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { WalletDto } from '@model/WalletDto'

import { useGuardedContext } from '@global/useGuardedContext'

import { useAccountReadContext } from './AccountContextContext'

interface WalletsReadContextImpl {
  wallets: WalletDto[] | undefined
}

interface WalletsWriteContextImpl {
  refreshUserWallets: () => Promise<WalletDto[] | undefined>
}

const WalletsReadContext = createContext<WalletsReadContextImpl | undefined>(undefined)
WalletsReadContext.displayName = 'WalletsReadContext'

const WalletsWriteContext = createContext<WalletsWriteContextImpl | undefined>(undefined)
WalletsWriteContext.displayName = 'WalletsWriteContext'

export function useWalletsReadContext(): WalletsReadContextImpl {
  return useGuardedContext(WalletsReadContext)
}

export function useWalletsWriteContext(): WalletsWriteContextImpl {
  return useGuardedContext(WalletsWriteContext)
}

interface Props {
  children: ReactNode
}

export function WalletsContextProvider({ children }: Props): React.ReactElement<any> {
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const { account } = useAccountReadContext()

  const { data: wallets } = useQuery({
    queryKey: queryKeys.wallet.list(),
    queryFn: () => apiClient.getWallets(),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    enabled: !!account,
  })

  const readContext = useMemo<WalletsReadContextImpl>(
    () => ({
      wallets,
    }),
    [wallets]
  )

  const writeContext = useMemo<WalletsWriteContextImpl>(() => {
    return {
      async refreshUserWallets() {
        try {
          const fetchedWallets = await queryClient.fetchQuery({
            queryKey: queryKeys.wallet.list(),
            queryFn: () => apiClient.getWallets(),
          })
          return fetchedWallets
        } catch (e: unknown) {
          console.error('Error during refreshWallets', e)
        }
      },
    }
  }, [apiClient, queryClient])

  return (
    <WalletsReadContext.Provider value={readContext}>
      <WalletsWriteContext.Provider value={writeContext}>{children}</WalletsWriteContext.Provider>
    </WalletsReadContext.Provider>
  )
}
